import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Swords, Check, X, Trophy } from "lucide-react";

/**
 * Duel Arena panel — sits in the Hub. Shows:
 *   - Nearby players (from the passed-in `players` list) with a Challenge button
 *   - Incoming pending challenges (accept/decline)
 *   - Weekly wins leaderboard
 *
 * Duels are resolved server-side on Accept (3-round deterministic exchange).
 * Winner gets a Duel Token and +1 win this ISO-week.
 */
export default function DuelArena({ ch, setCh, players }) {
  const [pending, setPending] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [busy, setBusy] = useState(false);
  const [resolution, setResolution] = useState(null); // {a_name,b_name,winner_name,a_hp,b_hp,log}

  const load = async () => {
    try {
      const [p, lb] = await Promise.all([api.duelPending(), api.duelLeaderboard()]);
      setPending(p.pending || []);
      setLeaderboard(lb.ranks || []);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);

  const challenge = async (p) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.duelChallenge(p.user_id);
      toast.success(`Challenge sent to ${p.name}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Challenge failed");
    } finally { setBusy(false); }
  };

  const accept = async (duel) => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await api.duelAccept(duel.duel_id);
      setResolution(res);
      const you = res.winner_id === ch.user_id;
      if (res.winner_id) {
        (you ? toast.success : toast.error)(`${res.winner_name} wins the duel!`);
      } else {
        toast("The duel ended in a draw.");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Accept failed");
    } finally { setBusy(false); }
  };

  const decline = async (duel) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.duelDecline(duel.duel_id);
      toast("Challenge declined.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Decline failed");
    } finally { setBusy(false); }
  };

  const nearby = (players || []).filter(p => p.name !== ch.name);

  return (
    <div className="panel p-3" data-testid="duel-arena">
      <div className="flex items-center gap-2 mb-2">
        <Swords className="w-3 h-3 text-[#8A0303]" strokeWidth={1.5} />
        <span className="font-heading text-[10px] tracking-widest uppercase text-[#8A0303]">
          Duel Arena
        </span>
      </div>

      {/* Pending challenges for me */}
      {pending.length > 0 && (
        <div className="mb-3 space-y-1.5" data-testid="duel-pending-list">
          {pending.map(d => (
            <div
              key={d.duel_id}
              data-testid={`duel-pending-${d.duel_id}`}
              className="flex items-center gap-2 p-2 border border-[#8A0303] bg-[rgba(138,3,3,0.10)]"
            >
              <span className="font-heading text-[10px] tracking-widest uppercase text-[#E55A00]">
                {d.challenger_name}
              </span>
              <span className="font-mono text-[9px] tracking-widest uppercase text-[#8c8c8c]">
                calls you out
              </span>
              <div className="ml-auto flex gap-1">
                <button
                  data-testid={`duel-accept-${d.duel_id}`}
                  onClick={() => accept(d)}
                  disabled={busy}
                  className="px-2 py-1 border border-[#6E8B3D] text-[#6E8B3D] font-heading text-[9px] tracking-widest uppercase flex items-center gap-1 disabled:opacity-30"
                >
                  <Check className="w-3 h-3" strokeWidth={1.5} /> Accept
                </button>
                <button
                  data-testid={`duel-decline-${d.duel_id}`}
                  onClick={() => decline(d)}
                  disabled={busy}
                  className="px-2 py-1 border border-[#333] text-[#666] font-heading text-[9px] tracking-widest uppercase flex items-center gap-1 disabled:opacity-30"
                >
                  <X className="w-3 h-3" strokeWidth={1.5} /> Decline
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Nearby players to challenge */}
      <div className="mb-3">
        <div className="font-mono text-[9px] tracking-widest uppercase text-[#666] mb-1">
          Nearby ({nearby.length})
        </div>
        {nearby.length === 0 && (
          <div className="text-[10px] italic text-[#555]">
            No other souls to duel right now.
          </div>
        )}
        <div className="flex flex-wrap gap-1.5">
          {nearby.map(p => (
            <button
              key={p.user_id}
              data-testid={`duel-challenge-${p.user_id}`}
              disabled={busy}
              onClick={() => challenge(p)}
              className="flex items-center gap-1 px-2 py-1 border border-[#333] hover:border-[#8A0303] font-heading text-[9px] tracking-widest uppercase text-[#B0ADA5] hover:text-[#E55A00] transition-colors disabled:opacity-30"
            >
              <Swords className="w-3 h-3" strokeWidth={1.5} /> {p.name}
            </button>
          ))}
        </div>
      </div>

      {/* Weekly leaderboard */}
      <div className="border-t border-[#262626] pt-2">
        <div className="flex items-center gap-1 mb-1">
          <Trophy className="w-3 h-3 text-[#E5C34A]" strokeWidth={1.5} />
          <span className="font-mono text-[9px] tracking-widest uppercase text-[#E5C34A]">
            This Week's Champions
          </span>
        </div>
        {leaderboard.length === 0 && (
          <div className="text-[10px] italic text-[#555]">No wins recorded this week.</div>
        )}
        <div className="space-y-1" data-testid="duel-leaderboard">
          {leaderboard.slice(0, 5).map((r, i) => (
            <div
              key={r.user_id}
              className="flex items-center gap-2 text-[10px] font-mono tracking-widest uppercase"
              data-testid={`duel-rank-${i + 1}`}
            >
              <span className="text-[#B89C3A] w-4">#{i + 1}</span>
              <span className="text-[#E2DFD8] flex-1 truncate">{r.name}</span>
              <span className="text-[#E5C34A]">{r.wins} W</span>
            </div>
          ))}
        </div>
      </div>

      {/* Resolution modal */}
      {resolution && (
        <div
          className="fixed inset-0 z-[65] flex items-center justify-center bg-[rgba(5,5,5,0.85)]"
          onClick={() => setResolution(null)}
          data-testid="duel-resolution"
        >
          <div
            className="panel p-4 max-w-sm w-[92%]"
            onClick={(e) => e.stopPropagation()}
            style={{ boxShadow: "0 0 30px rgba(138,3,3,0.35)" }}
          >
            <div className="font-heading text-xs tracking-[0.4em] uppercase text-[#8A0303] text-center mb-2">
              Duel Resolved
            </div>
            <div className="font-heading text-lg tracking-widest uppercase text-[#E5C34A] text-center mb-3">
              {resolution.winner_name === "Draw" ? "Blades Locked · Draw" : `${resolution.winner_name} · Victor`}
            </div>
            <div className="flex justify-around items-center mb-3">
              <div className="text-center">
                <div className="font-heading text-xs tracking-widest uppercase text-[#E2DFD8]">
                  {resolution.a_name}
                </div>
                <div className="font-mono text-[10px] text-[#8c8c8c]">HP {resolution.a_hp}</div>
              </div>
              <span className="font-heading text-[10px] tracking-widest text-[#666]">VS</span>
              <div className="text-center">
                <div className="font-heading text-xs tracking-widest uppercase text-[#E2DFD8]">
                  {resolution.b_name}
                </div>
                <div className="font-mono text-[10px] text-[#8c8c8c]">HP {resolution.b_hp}</div>
              </div>
            </div>
            <div className="space-y-0.5 max-h-32 overflow-y-auto border-t border-[#262626] pt-2">
              {resolution.log.map((line, i) => (
                <div key={i} className="font-body text-[11px] text-[#B0ADA5]">{line}</div>
              ))}
            </div>
            <button
              data-testid="duel-resolution-close"
              className="mt-3 w-full py-2 border border-[#333] hover:border-[#E5C34A] text-[#8c8c8c] hover:text-[#E5C34A] font-heading text-[10px] tracking-widest uppercase"
              onClick={() => setResolution(null)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
